import type { FtpSelectionResult } from "../athlete-profile/ftpSelection";
import type { RideParserDiagnostics } from "../ride-center/services/rideUploadPipeline";
import type { Ride } from "../ride-center/types/ride";
import type { UnifiedActivityAnalysisResult } from "./sessionAnalysis";
import type { ActivityFileStorage } from "./activityFileStorage";
import type { ActivitySession, ActivitySource, ActivitySourceStatus } from "./activitySources";
import type { TrainSubview } from "./trainViewState";

function tracePersistence(message: string, details?: unknown): void {
  if (!import.meta.env.DEV) return;
  const timestamp = new Date().toISOString();
  if (details === undefined) {
    console.log(`[persistence ${timestamp}] ${message}`);
    return;
  }
  console.log(`[persistence ${timestamp}] ${message}`, details);
}

function tracePersistenceError(message: string, error: unknown): void {
  if (!import.meta.env.DEV) return;
  console.error(`[persistence ${new Date().toISOString()}] ${message}`, error);
}

export const TRAIN_ACTIVITY_SESSION_POINTER_KEY = "johan-performance-lab.train.current-session-id";
export const TRAIN_ACTIVITY_SESSION_SCHEMA_VERSION = 1;

export type PersistedAnalysisStatus = "idle" | "complete" | "stale";

const TRAIN_ACTIVITY_DATABASE_NAME = "johan-performance-lab-train-persistence";
const TRAIN_ACTIVITY_DATABASE_VERSION = 1;
const TRAIN_ACTIVITY_STORE_NAME = "completed-sessions";

type PersistedSource = Omit<ActivitySource, "file" | "previewUrl"> & {
  fileName: string;
  fileSize: number;
  fileType: string;
  fileLastModified: number;
};

type PersistedActivitySession = Omit<ActivitySession, "primary" | "supportingRideSources" | "companionSources"> & {
  primary: PersistedSource | null;
  supportingRideSources: PersistedSource[];
  companionSources: PersistedSource[];
};

export type PersistedTrainActivityState = {
  schemaVersion: number;
  sessionId: string;
  savedAt: string;
  activitySession: PersistedActivitySession;
  parsedRide: Ride | null;
  unifiedAnalysis: UnifiedActivityAnalysisResult | null;
  analysisStatus: PersistedAnalysisStatus;
  analysisCompletedAt: string | null;
  lastFtpSelection: FtpSelectionResult | null;
  analysisFtpSelection: FtpSelectionResult | null;
  parserDiagnostics: RideParserDiagnostics | null;
  trainSubview: TrainSubview;
  selectedIntervalId: string | null;
  readinessState: {
    primarySourceStatus: ActivitySourceStatus | null;
    canAnalyze: boolean;
  };
};

export type PersistenceBackend = "indexeddb" | "memory";
export type PersistenceDatabaseStatus = "unknown" | "opening" | "open" | "failed";

export type PersistedSaveResult = {
  backend: PersistenceBackend;
  databaseStatus: PersistenceDatabaseStatus;
  sessionId: string;
  schemaVersion: number;
  savedAt: string;
  approximateSizeBytes: number;
};

export type PersistedLoadResult = {
  backend: PersistenceBackend;
  databaseStatus: PersistenceDatabaseStatus;
  sessionId: string | null;
  state: PersistedTrainActivityState | null;
  error: string | null;
};

type HydratedSource = {
  source: ActivitySource;
  fileAvailable: boolean;
};

type RestoredSessionPrimaryRecovery = {
  source: ActivitySource;
  file: File;
} | null;

export type RestoredActivitySessionResult = {
  session: ActivitySession;
  resumePrimary: RestoredSessionPrimaryRecovery;
};

const TRANSIENT_SOURCE_STATUSES: ActivitySourceStatus[] = ["selected", "queued", "validating", "parsing", "analyzing"];
const memoryPersistedSessions = new Map<string, PersistedTrainActivityState>();

export function isTransientSourceStatus(status: ActivitySourceStatus): boolean {
  return TRANSIENT_SOURCE_STATUSES.includes(status);
}

export function hasTransientSources(session: ActivitySession): boolean {
  const allSources = [session.primary, ...session.supportingRideSources, ...session.companionSources].filter((source): source is ActivitySource => source !== null);
  return allSources.some(source => isTransientSourceStatus(source.status));
}

export function isStableCompletedTrainState(input: {
  activitySession: ActivitySession;
  parsedRide: Ride | null;
  isParsingRide: boolean;
}): boolean {
  const primaryStatus = input.activitySession.primary?.status;
  const primaryStable = primaryStatus === "parsed" || primaryStatus === "ready";
  const hasParsedRide = input.parsedRide !== null && input.parsedRide.records.length > 0;
  const hasActivityMetadata = input.activitySession.activityMetadata !== null;
  return primaryStable && hasParsedRide && hasActivityMetadata && !input.isParsingRide;
}

function sourceToPersisted(source: ActivitySource): PersistedSource {
  const { file, previewUrl, ...persisted } = source;
  return {
    ...persisted,
    fileName: source.file.name,
    fileSize: source.file.size,
    fileType: source.file.type,
    fileLastModified: source.file.lastModified
  };
}

function activitySessionToPersisted(session: ActivitySession): PersistedActivitySession {
  return {
    ...session,
    primary: session.primary ? sourceToPersisted(session.primary) : null,
    supportingRideSources: session.supportingRideSources.map(sourceToPersisted),
    companionSources: session.companionSources.map(sourceToPersisted)
  };
}

function getStorage(): Storage | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

type OpenPersistenceResult = {
  backend: PersistenceBackend;
  databaseStatus: PersistenceDatabaseStatus;
  database: IDBDatabase | null;
};

let persistenceDatabaseStatus: PersistenceDatabaseStatus = "unknown";
let persistenceBackend: PersistenceBackend = "indexeddb";
let persistenceDatabase: IDBDatabase | null = null;
let persistenceInitialization: Promise<OpenPersistenceResult> | null = null;

export function getActivitySessionStoreStatus(): PersistenceDatabaseStatus {
  return persistenceDatabaseStatus;
}

export function getActivitySessionStoreBackend(): PersistenceBackend {
  return persistenceBackend;
}

export async function initializeActivitySessionStore(): Promise<OpenPersistenceResult> {
  if (persistenceDatabaseStatus === "open" && persistenceDatabase) {
    tracePersistence("open database returned early", { reason: "database is already open" });
    return {
      backend: persistenceBackend,
      databaseStatus: persistenceDatabaseStatus,
      database: persistenceDatabase
    };
  }

  if (persistenceInitialization) {
    tracePersistence("open database returned existing Promise", { reason: "database initialization is already in progress" });
    return persistenceInitialization;
  }

  persistenceDatabaseStatus = "opening";

  if (typeof indexedDB === "undefined") {
    tracePersistence("open database returned early", { reason: "IndexedDB is unavailable; using memory fallback" });
    persistenceBackend = "memory";
    persistenceDatabaseStatus = "failed";
    return {
      backend: persistenceBackend,
      databaseStatus: persistenceDatabaseStatus,
      database: null
    };
  }

  persistenceInitialization = new Promise(resolve => {
    tracePersistence("open database", {
      databaseName: TRAIN_ACTIVITY_DATABASE_NAME,
      version: TRAIN_ACTIVITY_DATABASE_VERSION
    });
    const request = indexedDB.open(TRAIN_ACTIVITY_DATABASE_NAME, TRAIN_ACTIVITY_DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(TRAIN_ACTIVITY_STORE_NAME)) {
        database.createObjectStore(TRAIN_ACTIVITY_STORE_NAME);
      }
    };
    request.onblocked = () => {
      tracePersistenceError("Promise could not open database: IndexedDB request blocked", request.error ?? new Error("IndexedDB open request was blocked."));
      persistenceBackend = "memory";
      persistenceDatabaseStatus = "failed";
      persistenceInitialization = null;
      resolve({
        backend: persistenceBackend,
        databaseStatus: persistenceDatabaseStatus,
        database: null
      });
    };
    request.onerror = () => {
      tracePersistenceError("Promise could not open database: IndexedDB request failed", request.error ?? new Error("IndexedDB open request failed."));
      persistenceBackend = "memory";
      persistenceDatabaseStatus = "failed";
      persistenceInitialization = null;
      resolve({
        backend: persistenceBackend,
        databaseStatus: persistenceDatabaseStatus,
        database: null
      });
    };
    request.onsuccess = () => {
      persistenceBackend = "indexeddb";
      persistenceDatabaseStatus = "open";
      persistenceDatabase = request.result;
      persistenceDatabase.onclose = () => {
        persistenceDatabaseStatus = "failed";
        persistenceDatabase = null;
      };
      persistenceInitialization = null;
      tracePersistence("database opened", {
        databaseName: TRAIN_ACTIVITY_DATABASE_NAME,
        status: persistenceDatabaseStatus
      });
      resolve({
        backend: persistenceBackend,
        databaseStatus: persistenceDatabaseStatus,
        database: persistenceDatabase
      });
    };
  });

  return persistenceInitialization;
}

async function openPersistenceDatabase(): Promise<OpenPersistenceResult> {
  tracePersistence("awaiting Promise: initializeActivitySessionStore");
  const initialized = await initializeActivitySessionStore();
  tracePersistence("Promise resolved: initializeActivitySessionStore", initialized.databaseStatus);
  if (initialized.database) {
    return initialized;
  }
  tracePersistence("open persistence database returned without database", {
    reason: "initialization did not provide an IndexedDB database",
    backend: persistenceBackend,
    status: persistenceDatabaseStatus
  });
  return {
    backend: persistenceBackend,
    databaseStatus: persistenceDatabaseStatus,
    database: null
  };
}

function stringifyWithBigInt(value: unknown): string {
  return JSON.stringify(value, (_key, nodeValue) => typeof nodeValue === "bigint" ? Number(nodeValue) : nodeValue);
}

function validatePersistedStateShape(value: unknown): { state: PersistedTrainActivityState | null; error: string | null } {
  if (!value || typeof value !== "object") {
    return { state: null, error: "Persisted value was not an object." };
  }
  const candidate = value as Partial<PersistedTrainActivityState>;
  if (candidate.schemaVersion !== TRAIN_ACTIVITY_SESSION_SCHEMA_VERSION) {
    return { state: null, error: `Unsupported persisted schema version: ${String(candidate.schemaVersion)}` };
  }
  if (!candidate.activitySession || typeof candidate.activitySession !== "object") {
    return { state: null, error: "Persisted activity session payload was missing." };
  }
  if (!candidate.sessionId || typeof candidate.sessionId !== "string") {
    return { state: null, error: "Persisted session ID was missing." };
  }
  return { state: candidate as PersistedTrainActivityState, error: null };
}

async function writeIndexedDbSession(database: IDBDatabase, state: PersistedTrainActivityState): Promise<void> {
  tracePersistence("awaiting Promise: IndexedDB completed-sessions write transaction", { sessionId: state.sessionId });
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(TRAIN_ACTIVITY_STORE_NAME, "readwrite");
    transaction.onerror = () => {
      const error = transaction.error ?? new Error("IndexedDB write failed.");
      tracePersistenceError("Promise rejected: IndexedDB completed-sessions write transaction", error);
      reject(error);
    };
    transaction.oncomplete = () => {
      tracePersistence("Promise resolved: IndexedDB completed-sessions write transaction", { sessionId: state.sessionId });
      resolve();
    };
    const store = transaction.objectStore(TRAIN_ACTIVITY_STORE_NAME);
    store.put(state, state.sessionId);
  });
}

async function readIndexedDbSession(database: IDBDatabase, sessionId: string): Promise<PersistedTrainActivityState | null> {
  tracePersistence("awaiting Promise: IndexedDB completed-sessions read request", { sessionId });
  return new Promise(resolve => {
    const transaction = database.transaction(TRAIN_ACTIVITY_STORE_NAME, "readonly");
    const store = transaction.objectStore(TRAIN_ACTIVITY_STORE_NAME);
    const request = store.get(sessionId);
    request.onerror = () => {
      tracePersistenceError("IndexedDB completed-sessions read request failed; resolving null", request.error ?? new Error("IndexedDB read request failed."));
      resolve(null);
    };
    request.onsuccess = () => {
      tracePersistence("Promise resolved: IndexedDB completed-sessions read request", {
        sessionId,
        statePresent: request.result !== undefined
      });
      resolve((request.result as PersistedTrainActivityState | undefined) ?? null);
    };
  });
}

async function readAllIndexedDbSessions(database: IDBDatabase): Promise<PersistedTrainActivityState[]> {
  return new Promise(resolve => {
    const transaction = database.transaction(TRAIN_ACTIVITY_STORE_NAME, "readonly");
    const store = transaction.objectStore(TRAIN_ACTIVITY_STORE_NAME);
    const request = store.getAll();
    request.onerror = () => {
      resolve([]);
    };
    request.onsuccess = () => {
      const states = (request.result as PersistedTrainActivityState[] | undefined) ?? [];
      resolve(states);
    };
  });
}

async function deleteIndexedDbSession(database: IDBDatabase, sessionId: string): Promise<void> {
  await new Promise<void>(resolve => {
    const transaction = database.transaction(TRAIN_ACTIVITY_STORE_NAME, "readwrite");
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => resolve();
    const store = transaction.objectStore(TRAIN_ACTIVITY_STORE_NAME);
    store.delete(sessionId);
  });
}

export async function savePersistedTrainActivityState(state: PersistedTrainActivityState): Promise<PersistedSaveResult> {
  tracePersistence("save begin", { sessionId: state.sessionId });
  tracePersistence("awaiting Promise: openPersistenceDatabase for save", { sessionId: state.sessionId });
  const opened = await openPersistenceDatabase();
  tracePersistence("Promise resolved: openPersistenceDatabase for save", {
    sessionId: state.sessionId,
    backend: opened.backend,
    status: opened.databaseStatus
  });
  if (opened.database) {
    await writeIndexedDbSession(opened.database, state);
    tracePersistence("parsed ride stored", {
      destination: "IndexedDB completed-sessions",
      sessionId: state.sessionId,
      present: state.parsedRide !== null
    });
    tracePersistence("canonical ride stored", {
      destination: "IndexedDB completed-sessions",
      sessionId: state.sessionId,
      present: state.activitySession.activityMetadata !== null
    });
  } else {
    memoryPersistedSessions.set(state.sessionId, state);
    tracePersistence("parsed ride stored", {
      destination: "memory fallback",
      sessionId: state.sessionId,
      present: state.parsedRide !== null
    });
    tracePersistence("canonical ride stored", {
      destination: "memory fallback",
      sessionId: state.sessionId,
      present: state.activitySession.activityMetadata !== null
    });
  }

  const storage = getStorage();
  storage?.setItem(TRAIN_ACTIVITY_SESSION_POINTER_KEY, state.sessionId);
  tracePersistence("session pointer updated", {
    sessionId: state.sessionId,
    persisted: storage !== null
  });

  tracePersistence("save complete", { sessionId: state.sessionId, savedAt: state.savedAt });

  return {
    backend: opened.backend,
    databaseStatus: opened.databaseStatus,
    sessionId: state.sessionId,
    schemaVersion: state.schemaVersion,
    savedAt: state.savedAt,
    approximateSizeBytes: stringifyWithBigInt(state).length
  };
}

export async function clearPersistedTrainActivityState(): Promise<void> {
  const storage = getStorage();
  const sessionId = storage?.getItem(TRAIN_ACTIVITY_SESSION_POINTER_KEY) ?? null;
  storage?.removeItem(TRAIN_ACTIVITY_SESSION_POINTER_KEY);

  if (!sessionId) {
    tracePersistence("clear persistence returned early", { reason: "current session pointer is missing" });
    return;
  }

  const opened = await openPersistenceDatabase();
  if (opened.database) {
    await deleteIndexedDbSession(opened.database, sessionId);
    return;
  }

  memoryPersistedSessions.delete(sessionId);
}

export async function loadPersistedTrainActivityState(): Promise<PersistedLoadResult> {
  tracePersistence("restore begin");
  const storage = getStorage();
  const sessionId = storage?.getItem(TRAIN_ACTIVITY_SESSION_POINTER_KEY) ?? null;
  tracePersistence("restore current session pointer", {
    sessionId,
    storageAvailable: storage !== null
  });
  tracePersistence("awaiting Promise: openPersistenceDatabase for restore", { sessionId });
  const opened = await openPersistenceDatabase();
  tracePersistence("Promise resolved: openPersistenceDatabase for restore", {
    sessionId,
    backend: opened.backend,
    status: opened.databaseStatus
  });

  if (!sessionId) {
    tracePersistence("restore returned early", { reason: "current session pointer is missing" });
    return {
      backend: opened.backend,
      databaseStatus: opened.databaseStatus,
      sessionId: null,
      state: null,
      error: null
    };
  }

  const loaded = opened.database
    ? await readIndexedDbSession(opened.database, sessionId)
    : memoryPersistedSessions.get(sessionId) ?? null;

  if (!loaded) {
    tracePersistence("restore returned early", {
      reason: "current session pointer has no persisted session payload",
      sessionId
    });
    return {
      backend: opened.backend,
      databaseStatus: opened.databaseStatus,
      sessionId,
      state: null,
      error: null
    };
  }

  const validated = validatePersistedStateShape(loaded);
  tracePersistence("restore parsed ride", {
    sessionId,
    present: validated.state?.parsedRide !== null && validated.state?.parsedRide !== undefined,
    records: validated.state?.parsedRide?.records.length ?? 0
  });
  tracePersistence("restore canonical ride", {
    sessionId,
    present: validated.state?.activitySession.activityMetadata !== null && validated.state?.activitySession.activityMetadata !== undefined
  });
  tracePersistence("restore complete", {
    sessionId,
    result: validated.state ? "success" : "invalid",
    error: validated.error
  });
  return {
    backend: opened.backend,
    databaseStatus: opened.databaseStatus,
    sessionId,
    state: validated.state,
    error: validated.error
  };
}

export async function loadPersistedTrainActivityStateBySessionId(sessionId: string): Promise<PersistedLoadResult> {
  const opened = await openPersistenceDatabase();
  if (!sessionId) {
    return {
      backend: opened.backend,
      databaseStatus: opened.databaseStatus,
      sessionId: null,
      state: null,
      error: "Session ID was not provided."
    };
  }

  const loaded = opened.database
    ? await readIndexedDbSession(opened.database, sessionId)
    : memoryPersistedSessions.get(sessionId) ?? null;

  if (!loaded) {
    return {
      backend: opened.backend,
      databaseStatus: opened.databaseStatus,
      sessionId,
      state: null,
      error: null
    };
  }

  const validated = validatePersistedStateShape(loaded);
  return {
    backend: opened.backend,
    databaseStatus: opened.databaseStatus,
    sessionId,
    state: validated.state,
    error: validated.error
  };
}

export async function listPersistedTrainActivityStates(): Promise<PersistedTrainActivityState[]> {
  const opened = await openPersistenceDatabase();
  const states = opened.database
    ? await readAllIndexedDbSessions(opened.database)
    : [...memoryPersistedSessions.values()];

  return states
    .map(value => validatePersistedStateShape(value).state)
    .filter((value): value is PersistedTrainActivityState => value !== null);
}

async function hydrateSource(source: PersistedSource, storage: ActivityFileStorage): Promise<HydratedSource> {
  tracePersistence("awaiting Promise: activity file restore", { sourceId: source.id });
  const restoredFile = await storage.get(source.id);
  tracePersistence("Promise resolved: activity file restore", {
    sourceId: source.id,
    fileAvailable: restoredFile !== null
  });
  const fileAvailable = restoredFile !== null;
  const file = restoredFile ?? new File([], source.fileName, { type: source.fileType, lastModified: source.fileLastModified });
  const previewUrl = source.role === "screenshot" && restoredFile && typeof URL !== "undefined" && typeof URL.createObjectURL === "function"
    ? URL.createObjectURL(restoredFile)
    : null;

  return {
    source: {
      ...source,
      file,
      previewUrl
    },
    fileAvailable
  };
}

export async function restoreActivitySession(session: PersistedActivitySession, storage: ActivityFileStorage): Promise<ActivitySession> {
  const primary = session.primary ? await hydrateSource(session.primary, storage) : null;
  const supportingRideSources = await Promise.all(session.supportingRideSources.map(source => hydrateSource(source, storage)));
  const companionSources = await Promise.all(session.companionSources.map(source => hydrateSource(source, storage)));

  return {
    ...session,
    primary: primary?.source ?? null,
    supportingRideSources: supportingRideSources.map(item => item.source),
    companionSources: companionSources.map(item => item.source)
  };
}

export async function restoreActivitySessionWithRecovery(session: PersistedActivitySession, storage: ActivityFileStorage): Promise<RestoredActivitySessionResult> {
  const primary = session.primary ? await hydrateSource(session.primary, storage) : null;
  if (!session.primary) {
    tracePersistence("primary activity file restore skipped", { reason: "persisted session has no primary source" });
  }
  tracePersistence("awaiting Promise: supporting activity files restore", { count: session.supportingRideSources.length });
  const supportingRideSources = await Promise.all(session.supportingRideSources.map(source => hydrateSource(source, storage)));
  tracePersistence("Promise resolved: supporting activity files restore", { count: supportingRideSources.length });
  tracePersistence("awaiting Promise: companion activity files restore", { count: session.companionSources.length });
  const companionSources = await Promise.all(session.companionSources.map(source => hydrateSource(source, storage)));
  tracePersistence("Promise resolved: companion activity files restore", { count: companionSources.length });

  let restoredPrimary = primary?.source ?? null;
  let resumePrimary: RestoredSessionPrimaryRecovery = null;
  if (restoredPrimary && primary && isTransientSourceStatus(restoredPrimary.status)) {
    if (primary.fileAvailable) {
      restoredPrimary = {
        ...restoredPrimary,
        status: "selected",
        note: "This activity upload was interrupted. Resuming validation."
      };
      resumePrimary = {
        source: restoredPrimary,
        file: restoredPrimary.file
      };
    } else {
      restoredPrimary = {
        ...restoredPrimary,
        status: "needs_reupload",
        note: "This activity upload was interrupted. Re-select the primary FIT file to continue."
      };
    }
  }

  return {
    session: {
      ...session,
      primary: restoredPrimary,
      supportingRideSources: supportingRideSources.map(item => item.source),
      companionSources: companionSources.map(item => item.source)
    },
    resumePrimary
  };
}

function createPersistableRide(ride: Ride | null): Ride | null {
  if (!ride) {
    return null;
  }
  return {
    ...ride,
    rawData: undefined,
    rawOneSecondRecords: undefined
  };
}

export function createPersistedTrainActivityState(input: {
  activitySession: ActivitySession;
  parsedRide: Ride | null;
  unifiedAnalysis: UnifiedActivityAnalysisResult | null;
  analysisStatus: PersistedAnalysisStatus;
  analysisCompletedAt: string | null;
  lastFtpSelection: FtpSelectionResult | null;
  analysisFtpSelection: FtpSelectionResult | null;
  parserDiagnostics: RideParserDiagnostics | null;
  trainSubview: TrainSubview;
  selectedIntervalId: string | null;
  canAnalyze: boolean;
}): PersistedTrainActivityState {
  return {
    schemaVersion: TRAIN_ACTIVITY_SESSION_SCHEMA_VERSION,
    sessionId: input.activitySession.id,
    savedAt: new Date().toISOString(),
    activitySession: activitySessionToPersisted(input.activitySession),
    parsedRide: createPersistableRide(input.parsedRide),
    unifiedAnalysis: input.unifiedAnalysis,
    analysisStatus: input.analysisStatus,
    analysisCompletedAt: input.analysisCompletedAt,
    lastFtpSelection: input.lastFtpSelection,
    analysisFtpSelection: input.analysisFtpSelection,
    parserDiagnostics: input.parserDiagnostics,
    trainSubview: input.trainSubview,
    selectedIntervalId: input.selectedIntervalId,
    readinessState: {
      primarySourceStatus: input.activitySession.primary?.status ?? null,
      canAnalyze: input.canAnalyze
    }
  };
}